import { useQuery } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { FormControlLabel, Switch, Typography } from '@material-ui/core'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import { setBPStagedConfig } from 'api/graphQL/reactiveVars/workspaceToggles'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { BP_MODE, NON_MODIFIER_PARAMETER_KEYS } from '../constants'
import { useBestPracticeManagementPanelStyles } from '../styles/useBestPracticeManagementPanelStyles'
import {
    BPMode,
    emptyParameterSelected,
    emptyParameterToggles,
    parameterConfig,
    ParameterKey,
    ParameterSelectedState,
    ParameterToggleState,
} from '../types/bpManagement.types'
import {
    buildFirmBPParameters,
    FirmDataShape,
} from '../utils/firmBPParameterUtils'
import { BestPracticeTipsList } from './BestPracticeTipsList'
import { ParameterListSection } from './ParameterListSection'

const buildEffectiveSelected = (
    parameterToggles: ParameterToggleState,
    parameterSelected: ParameterSelectedState
): ParameterSelectedState =>
    parameterConfig.reduce<ParameterSelectedState>(
        (acc, { key }) => {
            acc[key] = parameterToggles[key] ? parameterSelected[key] : []
            return acc
        },
        { ...emptyParameterSelected }
    )

/** Single selected value for a toggled parameter, or undefined otherwise. */
const extractSingleValue = (
    mode: BPMode,
    parameterToggles: ParameterToggleState,
    parameterSelected: ParameterSelectedState,
    key: ParameterKey
): string | undefined =>
    mode === BP_MODE.PARAMETER &&
    parameterToggles[key] &&
    parameterSelected[key].length > 0
        ? parameterSelected[key][0]
        : undefined

export const BestPracticeManagementPanelContent = () => {
    const classes = useBestPracticeManagementPanelStyles()
    const { currentUserActiveFirm } = useContext(AppBarContext)
    const firmId = currentUserActiveFirm?.firmId
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const showAuditTips =
        isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace

    const [isProjectParameterModeOn, setIsProjectParameterModeOn] = useState(
        false
    )
    const [isGlobalBPModeOn, setIsGlobalBPModeOn] = useState(false)
    const [parameterToggles, setParameterToggles] = useState<
        ParameterToggleState
    >(emptyParameterToggles)
    const [parameterSelected, setParameterSelected] = useState<
        ParameterSelectedState
    >(emptyParameterSelected)

    const { data: firmData } = useQuery<FirmDataShape>(
        FirmProjectParametersQuery,
        {
            variables: { firmId: firmId || '' },
            skip: !firmId,
        }
    )

    const firmOptions = useMemo(() => buildFirmBPParameters(firmData).options, [
        firmData,
    ])

    // Gated by parameterToggles, not just parameterSelected: turning off an
    // individual parameter's switch leaves its stale selected value(s) in
    // state (so the Select can re-show them if toggled back on), but that
    // value must not count toward "is anything actually selected".
    const effectiveParameterSelected = useMemo(
        () => buildEffectiveSelected(parameterToggles, parameterSelected),
        [parameterToggles, parameterSelected]
    )

    const hasSelectedParameter = Object.values(effectiveParameterSelected).some(
        (values) => values.length > 0
    )

    // Derived rather than synced via effect: once the last selected
    // parameter value is cleared, the toggle must read as off (not just
    // disabled) even though isProjectParameterModeOn itself hasn't reset.
    const isProjectParameterModeEffectivelyOn =
        isProjectParameterModeOn && hasSelectedParameter

    // Mirror local state into the staged-config reactive var consumed by
    // useApplyBPFromConfig.
    useEffect(() => {
        const mode: BPMode = isGlobalBPModeOn
            ? BP_MODE.GLOBAL
            : isProjectParameterModeEffectivelyOn
            ? BP_MODE.PARAMETER
            : BP_MODE.NONE

        const effectiveSelected =
            mode === BP_MODE.PARAMETER
                ? effectiveParameterSelected
                : emptyParameterSelected

        const projectGroup = extractSingleValue(
            mode,
            parameterToggles,
            parameterSelected,
            NON_MODIFIER_PARAMETER_KEYS.PROJECT_GROUP
        )
        const city = extractSingleValue(
            mode,
            parameterToggles,
            parameterSelected,
            NON_MODIFIER_PARAMETER_KEYS.CITY
        )
        const provinceState = extractSingleValue(
            mode,
            parameterToggles,
            parameterSelected,
            NON_MODIFIER_PARAMETER_KEYS.PROVINCE_STATE
        )

        setBPStagedConfig({
            mode,
            projectGroup,
            city,
            provinceState,
            parameterSelected: effectiveSelected,
        })
    }, [
        isGlobalBPModeOn,
        isProjectParameterModeEffectivelyOn,
        parameterToggles,
        parameterSelected,
    ])

    const handleProjectParameterToggle = (checked: boolean) => {
        setIsProjectParameterModeOn(checked)
        if (checked) {
            setIsGlobalBPModeOn(false)
            setParameterToggles((prev) => {
                const updated = { ...prev }
                ;(Object.keys(updated) as ParameterKey[]).forEach((key) => {
                    if (parameterSelected[key].length > 0) updated[key] = true
                })
                return updated
            })
        } else {
            setParameterToggles(emptyParameterToggles)
        }
    }

    const handleGlobalBPToggle = (checked: boolean) => {
        setIsGlobalBPModeOn(checked)
        if (checked) {
            setIsProjectParameterModeOn(false)
            setParameterToggles(emptyParameterToggles)
        }
    }

    const handleParameterToggle = (key: ParameterKey) => {
        setParameterToggles((prev) => {
            const newChecked = !prev[key]
            if (newChecked) {
                setIsProjectParameterModeOn(true)
                setIsGlobalBPModeOn(false)
                // Default to first option so the user isn't forced into an
                // explicit "Select..." step.
                setParameterSelected((prevSelected) => {
                    if (prevSelected[key].length > 0) return prevSelected
                    const firstOption = firmOptions[key]?.[0]
                    if (!firstOption) return prevSelected
                    return { ...prevSelected, [key]: [firstOption] }
                })
            }
            return { ...prev, [key]: newChecked }
        })
    }

    const handleParameterSelectedChange = (
        key: ParameterKey,
        values: string[]
    ) => {
        setParameterSelected((prev) => ({ ...prev, [key]: values }))
        if (values.length === 0) {
            setParameterToggles((prev) => ({ ...prev, [key]: false }))
        }
    }

    return (
        <div className={classes.contentRoot}>
            {showAuditTips && (
                <>
                    <div className={classes.auditTipsScrollContainer}>
                        <BestPracticeTipsList />
                    </div>
                    <div className={classes.divider} />
                </>
            )}

            <Typography className={classes.headerText}>
                You can apply the selected Best Practice parameters to any
                content by <b>double-clicking</b> it on the left.
            </Typography>

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isProjectParameterModeEffectivelyOn}
                control={<Switch color="primary" size="small" />}
                disabled={!hasSelectedParameter}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Project Parameter Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) =>
                    handleProjectParameterToggle(checked)
                }
            />

            <div className={classes.divider} />

            <ParameterListSection
                firmOptions={firmOptions}
                parameterToggles={parameterToggles}
                parameterSelected={parameterSelected}
                onToggle={handleParameterToggle}
                onSelectedChange={handleParameterSelectedChange}
            />

            <div className={classes.divider} />

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isGlobalBPModeOn}
                control={<Switch color="primary" size="small" />}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Global Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) => handleGlobalBPToggle(checked)}
            />
        </div>
    )
}
